import { useCallback } from 'react';
import { setTaskStatus } from '@apis/schedule';
import { ChecklistIncompleteError } from '@apis/checklists';
import { usePullPlan } from './usePullPlan';
import type { SiteId, TaskId, TaskStatus } from '@interfaces/IScheduler';
import { useImmediateWritePersist } from './useImmediateWritePersist';
import { useOwnPlanWriteAck } from './useOwnPlanWriteAck';

/**
 * Immediate-write persistence for a task's PLAN status (mark done / block /
 * reopen). It is a plan field with an operational write cadence — a row action,
 * not a Gantt edit ending in Save — so it gets its own targeted PUT.
 *
 * The write BUMPS the schedule version server-side: the plan really did change,
 * and a Gantt holding a stale copy must rebase rather than overwrite the flag on
 * its next whole-array Save. The answer names the version it produced, and
 * `useOwnPlanWriteAck` acknowledges it as this page's own — otherwise the
 * version poll would read our own bump as another editor's and freeze the page.
 *
 * Rides the shared `immediateWriteChannel` on one channel per task, so rapid
 * re-picks coalesce to the final status and stay serialized. No-op when
 * `scheduleNumber` is absent (Router-free tests) — the store still updates.
 *
 * A done the SERVER refuses for an open checklist (someone reopened a check
 * this page had not seen yet) is an answer, not a failure: nothing retries.
 * The plan is re-read — which undoes the optimistic done — and
 * `onChecklistRefused` tells the user why.
 */
export function useTaskStatusPersist(
  siteId: SiteId,
  scheduleNumber: string | undefined,
  taskId: TaskId,
  onChecklistRefused?: () => void,
): (status: TaskStatus) => void {
  const { write } = useImmediateWritePersist(siteId, scheduleNumber, taskId);
  const pullPlan = usePullPlan(siteId);
  const ackOwnWrite = useOwnPlanWriteAck(siteId);

  return useCallback(
    (status: TaskStatus) => {
      write<TaskStatus>(['status'], status, (s, ctx) =>
        ackOwnWrite(
          ctx,
          () => setTaskStatus(ctx.siteId, ctx.scheduleNumber, ctx.taskId, s),
          (task) => ({ ...task, status: s }),
        ).catch(async (err: unknown) => {
          if (!(err instanceof ChecklistIncompleteError)) throw err;
          await pullPlan(ctx.scheduleNumber);
          onChecklistRefused?.();
        }),
      );
    },
    [write, ackOwnWrite, pullPlan, onChecklistRefused],
  );
}
